import { where } from 'firebase/firestore';
import { values } from 'ramda';
import { Suppliers, Weddings } from '@bridebook/models';
import { toMillis } from '@bridebook/toolbox/src/time-utils/time-utils';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from '@bridebook/web/lib/utils';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';
import { BBOrCustomSupplier } from './use-supplier';
import { chunk } from 'lodash';

const SUPPLIERS_QUERY_CACHE_TIME = toMillis(15, 'min');
const FIRESTORE_IN_LIMIT = 10; // Firestore 'in' query limit

/**
 * Fetches multiple suppliers by IDs from Firestore in batches.
 * Tries BB suppliers first, then falls back to custom suppliers for any missing IDs.
 */
export const useBBSuppliers = (params: { supplierIds: string[] }) => {
  const weddingId = useSelector(getWeddingId);

  const { data, isLoading } = useQuery(
    ['bb-suppliers', params.supplierIds],
    async () => {
      if (!params.supplierIds || params.supplierIds.length === 0) return [];

      // Remove duplicates
      const uniqueIds = [...new Set(params.supplierIds)];

      // Chunk IDs to respect Firestore 'in' limit
      const idChunks = chunk(uniqueIds, FIRESTORE_IN_LIMIT);

      // Fetch BB suppliers in batches
      const bbSuppliersResults = await Promise.all(
        idChunks.map((ids) =>
          Suppliers._.query([where('id', 'in', ids)])
            .get()
            .then(values),
        ),
      );

      const bbSuppliers = bbSuppliersResults.flat();

      // Find missing IDs (these might be custom suppliers)
      const foundIds = new Set(bbSuppliers.map((s) => s.id));
      const missingIds = uniqueIds.filter((id) => !foundIds.has(id));

      // Fetch custom suppliers for missing IDs
      let customSuppliers: BBOrCustomSupplier[] = [];
      if (missingIds.length > 0) {
        const customChunks = chunk(missingIds, FIRESTORE_IN_LIMIT);
        const customSuppliersResults = await Promise.all(
          customChunks.map((ids) =>
            Weddings._.getById(weddingId)
              .Suppliers.query([where('id', 'in', ids)])
              .get()
              .then(values)
              .catch(() => [] as BBOrCustomSupplier[]),
          ),
        );
        customSuppliers = customSuppliersResults.flat();
      }

      return [...bbSuppliers, ...customSuppliers];
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: SUPPLIERS_QUERY_CACHE_TIME,
      staleTime: SUPPLIERS_QUERY_CACHE_TIME,
    },
  );

  return { data, isLoading };
};
